import { useImperativeHandle } from "react";
import { Accordion, Card } from "react-bootstrap";
import { get, useFormContext } from "react-hook-form";
import { useSearchParams, useParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";

import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import { ReactComponent as CheckIcon } from "assets/images/icons/Check.svg";
import { ReactComponent as ExclamationIcon } from "assets/images/icons/Exclamation.svg";
import { QuotationFormStepValidationTypes, QuotationFormStepTypes } from "constants/";
import { useGetQuotationFormConfig } from "api/queries/quotation-form/useGetQuotationFormConfig";
import AdditionalInformation from "./components/AdditionalInformation";
import Attachments from "./components/Attachments";
import ServiceConfirmation from "./components/ServiceConfirmation";
import CustomServiceConfirmation from "./components/CustomServiceConfirmation";
import { QuotationFormState } from "../../QuotationFormState";
import FormHr from "../../../../shipper/RequestForm/components/Common/FormHr";

type MiscFormProps = {
  childRef: React.MutableRefObject<any>;
  state: QuotationFormState;
};

export default function MiscForm({ childRef, state }: MiscFormProps) {
  const {
    watch,
    formState: { errors },
  } = useFormContext();
  const [searchParams] = useSearchParams();
  const selectedFreightMode: string = watch("service.freight_mode");

  const id = useParams().id?.toUpperCase();
  const { data: quotationConfigData } = useGetQuotationFormConfig(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const currentActiveStep = searchParams.get("active_step");
  const miscFields = (QuotationFormStepValidationTypes[QuotationFormStepTypes.MISC] ?? []) as string[];
  const hasError = miscFields.some((item) => get(errors, item));

  useImperativeHandle(childRef, () => ({}));

  return (
    <Card className="mb-3 form-container">
      <Accordion.Toggle as={Card.Header} className="accordion-header" eventKey={QuotationFormStepTypes.MISC}>
        <div className="d-flex align-items-center">
          {(hasError || state.draftSaved[QuotationFormStepTypes.MISC]) && (
            <div className={`valid-status ${hasError ? "error" : ""}`}>{hasError ? <ExclamationIcon /> : <CheckIcon />}</div>
          )}
          <h5>
            <FormattedMessage id="general.miscellaneous" defaultMessage="Miscellaneous" />
          </h5>
        </div>
        <SortIcon
          fill="#000"
          width={8}
          height={7}
          transform={`rotate(${currentActiveStep === QuotationFormStepTypes.MISC ? 180 : 0})`}
        />
      </Accordion.Toggle>
      <Accordion.Collapse eventKey={QuotationFormStepTypes.MISC}>
        <Card.Body>
          {quotationConfigData?.service_confirmation && <ServiceConfirmation quotationConfigData={quotationConfigData} />}
          {quotationConfigData?.custom_service_confirmation &&
            quotationConfigData?.custom_service_confirmation?.[selectedFreightMode] && (
              <CustomServiceConfirmation quotationConfigData={quotationConfigData} />
            )}
          <AdditionalInformation />
          <FormHr />
          <Attachments quotationConfigData={quotationConfigData} />
        </Card.Body>
      </Accordion.Collapse>
    </Card>
  );
}
